import React, { useCallback } from "react";
import { SoftwareSettingsMetadata, SettingsTypeEnumSchema, PathTypeSchema } from "@domain/schemas/BI";
import { UploadSettingsService } from "@application/useCases/softwareSettings/UploadSettingsService";
import { getSettingsSection, getSoftwareAccent } from "@domain/software";
import { useInjection } from "@presentation/components/dependencyInjection";
import { useNotifications } from "@presentation/components/notifications";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { SingleFileUpload } from "@presentation/components/uploadDestination/SingleFileUpload";
import type { UploadDestination } from "@presentation/components/uploadDestination/UploadDestinationPickerDialog";
import { useShouldShowEngineName } from "./useLicensedSoftware";

interface UploadSettingsProperties {
  onUploadSuccess?: (uploadedSettings: SoftwareSettingsMetadata) => void;
  settingsType: SettingsTypeEnumSchema;
  initialDestination?: Partial<UploadDestination>;
}

const UploadSettings: React.FC<UploadSettingsProperties> = ({ onUploadSuccess, settingsType, initialDestination }) => {
  const service = useInjection(UploadSettingsService);
  const { pushNotification } = useNotifications();
  const softwareVersion = useJobSetupStore((state) => state.CreateJobState.softwareVersion);
  const software = useJobSetupStore((state) => state.CreateJobState.software);
  const projectId = useWorkspaceContextStore((state) => state.activeProjectId);
  const section = getSettingsSection(software, settingsType);
  const isSoftwareScoped = section?.softwareScoped ?? true;
  const showEngineName = useShouldShowEngineName();
  const versionLabel = showEngineName ? `${software ?? "Spectronaut"} version` : "version";
  const accent = isSoftwareScoped ? getSoftwareAccent(software) : { background: "#f1f5f9", border: "#cbd5e1", text: "#475569" };
  const checkFileExists = useCallback(
    (name: string, rootPath: string | undefined, folder: string | undefined) => service.checkSettingsFileExistsAsync(projectId ?? "", name, rootPath, folder),
    [service, projectId],
  );
  return (
    <SingleFileUpload
      key={`${projectId}:${settingsType}:${software}:${softwareVersion}`}
      projectId={projectId}
      pathType={PathTypeSchema.SoftwareSettings}
      initialDestination={initialDestination}
      checkFileExists={checkFileExists}
      prerequisites={softwareVersion ? undefined : `Select a ${versionLabel} before uploading settings.`}
      upload={async (file, destination, onProgress) => {
        if (!projectId || !softwareVersion) throw new Error("Select a project and software version before uploading settings.");
        const uploaded = await service.uploadSettingsAsync(file, softwareVersion, onProgress, settingsType, projectId, software, destination.rootPath, destination.folder);
        pushNotification("The settings file was successfully uploaded.", "success", 10_000);
        onUploadSuccess?.(uploaded);
      }}
    >
      <div role="note" style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 6, border: `1px solid ${accent.border}`, backgroundColor: accent.background, color: accent.text }}>
        Uploading <strong>{section?.label ?? settingsType}</strong>
        {!isSoftwareScoped && " — shared across search engines"}
        {isSoftwareScoped && showEngineName && (
          <>
            {" "}
            for <strong>{software ?? "Spectronaut"}</strong>
          </>
        )}
        .
      </div>
    </SingleFileUpload>
  );
};

export default UploadSettings;
